/**
 * THE COC WORKSTATION (COC-STATION-V1, V4-P1) -- the COC role's daily home,
 * the first surface born under the 360 law (E-2) + the twelve quality gates
 * (E-3): MY WORK header counts + the work queue table, every action IN
 * PLACE (claim / release via the proven ownership writer; the Lead-360
 * quick-view DRAWER; the record-outcome drawer) -- acting on a row never
 * navigates away; the full Lead-360 page stays one click deeper.
 *
 * Honest states: loading / error (server message verbatim) / empty /
 * denied; the capped read is surfaced, never silent. SLA cells render the
 * V4-P0 classifier's output verbatim (chips + humanized age via the ONE
 * leakage vocabulary). Timestamps render through the ONE IST formatter.
 * Zero clinical fields, zero payment metadata on this wire (server-carved).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { clinicLabelOf } from '@/dashboard/clinic-context';
import { Link } from 'react-router-dom';
import {
  claimLead,
  fetchCocStation,
  releaseLead,
  type CocStationRowView,
  type CocStationView,
  type StationSegmentView,
} from '@/dashboard/api';
import DashboardLoadError from '@/dashboard/DashboardLoadError';
import { portalErrorStatus, safeErrorMessage } from '@/lib/portal-api';
import LeadQuickViewHost from '@/dashboard/leads/LeadQuickViewHost';
import RecordOutcomeDialog from '@/dashboard/coc/RecordOutcomeDialog';
import {
  STATION_SEGMENTS,
  callbackIsDue,
  primaryActionFor,
  rowsForSegment,
  slaChipAge,
  slaChipTitle,
  stationOpeningLine,
  stationSlaBreachCounts,
  stationTiles,
} from '@/dashboard/coc/station-view';
import {
  InboundCallPanel,
  SlaBreachStrip,
  canReachLeadDetail,
} from '@/dashboard/lead-cockpit/CallcenterCommandCenter';
import { useDashboardMeContext } from '@/dashboard/me-context';
import {
  collapsedTileLabels,
  quietZeroLine,
  tileRendersAsCard,
} from '@/dashboard/callcenter/callcenter-workspace-view';
import { OpeningLine } from '@/dashboard/ui';
import { formatIstDateTime } from '@/lib/datetime-ist';

export type StationLoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string; status?: number }
  | { kind: 'ready'; view: CocStationView };

type DrawerState =
  | { kind: 'none' }
  | { kind: 'quick_view'; row: CocStationRowView }
  | { kind: 'record_outcome'; row: CocStationRowView };

export interface CocStationSectionProps {
  /** TEST-ONLY state injection (react-dom/server renders cannot run
   *  effects); production callers render with no props and fetch live. */
  readonly initialState?: StationLoadState;
}

export default function CocStationSection({
  initialState,
}: CocStationSectionProps = {}) {
  const [state, setState] = useState<StationLoadState>(
    initialState ?? { kind: 'loading' },
  );
  const [segment, setSegment] = useState<StationSegmentView | 'all'>('mine');
  const [clinicFilter, setClinicFilter] = useState<number | 'all'>('all');
  const [drawer, setDrawer] = useState<DrawerState>({ kind: 'none' });
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyLeadId, setBusyLeadId] = useState<number | null>(null);
  // Hook order: read /me with the other hooks, ABOVE every early return.
  const meCtx = useDashboardMeContext();

  const load = useCallback(() => {
    setState({ kind: 'loading' });
    void fetchCocStation()
      .then((view) => setState({ kind: 'ready', view }))
      .catch((e: unknown) =>
        setState({
          kind: 'error',
          message: safeErrorMessage(e, 'Something went wrong. Retry.'),
          ...(portalErrorStatus(e) !== undefined
            ? { status: portalErrorStatus(e) }
            : {}),
        }),
      );
  }, []);

  useEffect(() => {
    if (initialState !== undefined) return;
    load();
  }, [initialState, load]);

  const nowMs = useMemo(
    () =>
      state.kind === 'ready'
        ? Date.parse(state.view.generatedAtIso) || Date.now()
        : Date.now(),
    [state],
  );

  const doClaim = (row: CocStationRowView) => {
    if (busyLeadId !== null) return;
    setBusyLeadId(row.leadId);
    setActionError(null);
    void claimLead(row.leadId)
      .then(() => load())
      .catch((e: unknown) => {
        setActionError(safeErrorMessage(e, 'Something went wrong. Retry.'));
      })
      .finally(() => setBusyLeadId(null));
  };

  const doRelease = (row: CocStationRowView) => {
    if (busyLeadId !== null) return;
    setBusyLeadId(row.leadId);
    setActionError(null);
    void releaseLead(row.leadId)
      .then(() => load())
      .catch((e: unknown) => {
        setActionError(safeErrorMessage(e, 'Something went wrong. Retry.'));
      })
      .finally(() => setBusyLeadId(null));
  };

  if (state.kind === 'loading') {
    return (
      <section className="coc-station" data-testid="coc-station-loading">
        <div className="loading">Loading your work queue...</div>
      </section>
    );
  }
  if (state.kind === 'error') {
    return (
      <section className="coc-station" data-testid="coc-station-error">
        <DashboardLoadError
          message={state.message}
          {...(state.status !== undefined ? { status: state.status } : {})}
        />
      </section>
    );
  }

  const { view } = state;
  const tiles = stationTiles(view.counts);
  const opening = stationOpeningLine(
    view.counts,
    view.rows,
    nowMs,
    view.capped,
  );
  // The collapse decision is computed BEFORE the JSX, not accumulated while
  // children render -- #680's own reasoning, kept: render order is not a
  // contract worth depending on, and the whole decision reads in one place.
  const quietLine = quietZeroLine(
    collapsedTileLabels(
      tiles.map((t) => ({ label: t.label, value: t.value, active: false })),
    ),
  );
  const clinicScoped =
    clinicFilter === 'all'
      ? view.rows
      : view.rows.filter((r) => r.clinicId === clinicFilter);
  const rows = rowsForSegment(clinicScoped, segment);
  // NEVER-BOUNCE RULE: the call panel's caller links go through the ONE
  // decision function the command centre uses (leadsWorkspaceEnabled on
  // /me), never a second rule and never a hardcoded true -- a COC whose
  // org has the leads workspace closed gets non-navigating text, not a
  // link that bounces them back here.
  const leadDetailReachable = canReachLeadDetail(meCtx?.me ?? null);

  return (
    <section className="coc-station" data-testid="coc-station" aria-label="My work">
      <header className="coc-station__head">
        <div>
          {/* The eyebrow said "COC station" directly above a title that says
              "My work", inside a COC nav that is already highlighted. Three
              statements of where you are; one is enough. */}
          <h2 className="coc-station__title">My work</h2>
        </div>
        <span className="coc-station__generated">
          As of {formatIstDateTime(view.generatedAtIso)}
        </span>
      </header>

      {/* S1 OPENING LINE -- THE REGISTERED SHARED PRIMITIVE, not a second
          one (founder instruction 2026-09-01; docs/EXPERIENCE-LANGUAGE.md,
          THE FIVE SIGNATURES). One deterministic story above the hero
          figures, every clause proven by a figure rendered below it. The
          clauses are composed by station-view's pure `stationOpeningLine`
          (the primitive is presentation-only by its own contract) and read
          view.rows UNFILTERED, because the figures they explain count the
          caller's whole scope. */}
      <OpeningLine
        state={opening.state}
        clauses={opening.clauses}
        testId="coc-station-opening-line"
      />

      {/* QUIET ZEROS -- PER TILE, NOT PER SCREEN (founder corrective, #683
          held). The earlier revision only went quiet when EVERY figure was
          zero, so on a real shift -- some counts live, some at zero -- the
          zeros still drew full cards and the screen carried its old weight.
          A zero earns a LINE; a non-zero earns a CARD; the decision is made
          per tile.

          THE RULE IS #680's, REUSED RATHER THAN REWRITTEN.
          `tileRendersAsCard` / `collapsedTileLabels` / `quietZeroLine` are
          the pure, tested functions COMMAND-CENTER-CRAFT-V2 shipped for this
          exact problem on the call-centre strip; importing them is the whole
          point of their being pure. None of this screen's tiles is a filter,
          so `active` is always false here -- the "keep the card you are
          standing on" exception has nothing to apply to. */}
      <div
        className="coc-station__tiles coc-station__tiles--capped"
        data-testid="coc-station-tiles"
      >
        {tiles
          .filter((t) => tileRendersAsCard(t.value, false))
          .map((t) => (
            <div
              key={t.key}
              className={`coc-station__tile coc-station__tile--${t.tone}`}
              data-testid={`station-tile-${t.key}`}
            >
              <span className="coc-station__tile-value">{t.value}</span>
              <span className="coc-station__tile-label">{t.label}</span>
            </div>
          ))}
      </div>

      {quietLine !== null ? (
        <p className="workspace-strip-clear" data-testid="coc-station-quiet-zeros">
          {quietLine}
        </p>
      ) : null}

      {view.capped ? (
        <p className="panel-caveat" data-testid="coc-station-capped">
          Large queue: this view is showing the newest slice of your scope, so
          counts are a lower bound.
        </p>
      ) : null}

      <div className="coc-station__filters">
        <div className="coc-station__segments" role="tablist" aria-label="Queue segment">
          {STATION_SEGMENTS.map((s) => (
            <button
              key={s.key}
              type="button"
              role="tab"
              aria-selected={segment === s.key}
              className={`coc-station__segment${segment === s.key ? ' coc-station__segment--active' : ''}`}
              onClick={() => setSegment(s.key)}
              data-testid={`station-segment-${s.key}`}
            >
              {s.label}
              {/* Every chip carries its own count, the way the Follow-ups
                  register does. Computed from the SAME filtered array the
                  table renders (rowsForSegment over clinicScoped), so a chip
                  can never disagree with the list it produces. */}
              <span className="coc-station__segment-count">
                {rowsForSegment(clinicScoped, s.key).length}
              </span>
            </button>
          ))}
        </div>
        {view.clinics.length > 1 ? (
          <select
            className="coc-station__clinic-filter"
            value={clinicFilter === 'all' ? 'all' : String(clinicFilter)}
            onChange={(e) =>
              setClinicFilter(
                e.target.value === 'all' ? 'all' : Number(e.target.value),
              )
            }
            aria-label="Filter by clinic"
            data-testid="station-clinic-filter"
          >
            <option value="all">All my clinics</option>
            {view.clinics.map((c) => (
              <option key={c.clinicId} value={String(c.clinicId)}>
                {clinicLabelOf(c.clinicLabel)}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {actionError !== null ? (
        <p className="coc-station__error" role="alert" data-testid="station-action-error">
          {actionError}
        </p>
      ) : null}

      {segment === 'converted_link_pending' ? (
        <p className="panel-caveat" data-testid="station-link-pending-note">
          Converted leads with no patient record link yet - open each record
          and link or register the patient (never auto-linked). This is
          identity-repair work, not contact work.
        </p>
      ) : null}

      {/* SLA-ONE-DERIVATION-AND-STATION-STRIP-V1 (Refs #315): the per-class
          breach strip over EXACTLY the rows the table below renders (strip ==
          list under every segment + clinic filter), derived client-side from
          the rows' classifier-verbatim slaChips -- the SAME ONE V4-P0
          classifier output the "Past SLA (mine)" tile counts distinct leads
          from (one derivation, two aggregations). Absent when zero. */}
      <SlaBreachStrip counts={stationSlaBreachCounts(rows)} />

      {rows.length === 0 ? (
        <p className="coc-station__empty" data-testid="coc-station-empty">
          {segment === 'mine'
            ? 'No work assigned to you right now. Check the Unclaimed queue.'
            : 'Nothing here right now.'}
        </p>
      ) : (
        <div className="coc-station__table-wrap">
          <table className="table coc-station__table" data-testid="coc-station-table">
            <thead>
              <tr>
                <th scope="col">Lead</th>
                <th scope="col">Clinic</th>
                <th scope="col">Interest</th>
                <th scope="col">Stage</th>
                <th scope="col">SLA</th>
                <th scope="col">Assigned to</th>
                <th scope="col">Last action</th>
                <th scope="col">Next action</th>
                <th scope="col">Callback (IST)</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const primary = primaryActionFor(row);
                const due = callbackIsDue(row, nowMs);
                return (
                  <tr key={row.leadId} data-testid={`station-row-${row.leadId}`}>
                    <td>
                      <button
                        type="button"
                        className="coc-station__name-link"
                        onClick={() => setDrawer({ kind: 'quick_view', row })}
                        data-testid={`station-open-${row.leadId}`}
                      >
                        {row.displayName || `Lead #${row.leadId}`}
                      </button>
                      {row.convertedLinkPending ? (
                        <span
                          className="ac-badge ac-badge--pending"
                          data-testid={`station-link-pending-${row.leadId}`}
                        >
                          Patient link pending
                        </span>
                      ) : null}
                    </td>
                    <td>{row.clinicLabel ?? '-'}</td>
                    <td>{row.serviceInterest ?? '-'}</td>
                    <td>{row.stageLabel ?? '-'}</td>
                    <td>
                      {row.slaChips.length === 0 ? (
                        <span className="coc-station__sla-ok">Within SLA</span>
                      ) : (
                        <span className="coc-station__sla-chips">
                          {row.slaChips.map((c) => (
                            <span
                              key={c.breachClass}
                              className={`ac-badge ${c.severity === 'red' ? 'ac-badge--danger' : 'ac-badge--pending'}`}
                              title={slaChipTitle(c.breachClass)}
                              data-testid={`station-sla-${row.leadId}-${c.breachClass}`}
                            >
                              {slaChipTitle(c.breachClass)} - {slaChipAge(c.ageMinutes)}
                            </span>
                          ))}
                        </span>
                      )}
                    </td>
                    <td>
                      {row.assignedLabel ??
                        (row.assignedStaffId === null ? (
                          <span className="ac-badge ac-badge--neutral">Unclaimed</span>
                        ) : (
                          'A teammate'
                        ))}
                    </td>
                    <td>
                      {row.lastAction !== null ? (
                        <span className="coc-station__last-action">
                          <span>{row.lastAction.label}</span>
                          {row.lastAction.atIso !== null ? (
                            <span className="coc-station__when">
                              {formatIstDateTime(row.lastAction.atIso)}
                            </span>
                          ) : null}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>{row.nextAction.text}</td>
                    <td>
                      {row.callbackDueAtIso !== null ? (
                        <span
                          className={due ? 'coc-station__callback--due' : undefined}
                          data-testid={`station-callback-${row.leadId}`}
                        >
                          {formatIstDateTime(row.callbackDueAtIso)}
                          {due ? ' (due)' : ''}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="coc-station__actions">
                      {primary.kind === 'claim' ? (
                        <button
                          type="button"
                          className="ac-btn ac-btn--primary ac-btn--sm"
                          onClick={() => doClaim(row)}
                          disabled={busyLeadId !== null}
                          data-testid={`station-claim-${row.leadId}`}
                        >
                          {busyLeadId === row.leadId ? 'Claiming...' : 'Claim'}
                        </button>
                      ) : primary.kind === 'record_outcome' ? (
                        <button
                          type="button"
                          className="ac-btn ac-btn--primary ac-btn--sm"
                          onClick={() => setDrawer({ kind: 'record_outcome', row })}
                          data-testid={`station-record-${row.leadId}`}
                        >
                          Record outcome
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="ac-btn ac-btn--secondary ac-btn--sm"
                          onClick={() => setDrawer({ kind: 'quick_view', row })}
                          data-testid={`station-open-action-${row.leadId}`}
                        >
                          Open
                        </button>
                      )}
                      {row.isMine ? (
                        <button
                          type="button"
                          className="ac-btn ac-btn--ghost ac-btn--sm"
                          onClick={() => doRelease(row)}
                          disabled={busyLeadId !== null}
                          data-testid={`station-release-${row.leadId}`}
                        >
                          Release
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/*
        CALLS THAT CAME IN -- COC-STATION-INBOUND-CALLS-V1.

        PLACEMENT IS THE RULING: BELOW the work queue, never above it. The
        COC's own work is the rows above; inbound calls are the call
        centre's work, shown here as SUPERVISION SIGNAL. Ordering a
        supervisor's screen by somebody else's queue would put the COC's
        own SLA-breaching rows under a block they cannot act on.

        MONITORING ONLY (variant="monitor"): no next-action column. The
        repo's own tenant ruling is the authority -- days 0..boundary-1 the
        visited clinic's front desk owns every follow-up and the COC is
        "VISIBLE BUT NOT OWNED. READ ONLY" (R2,
        docs/requirements/partha-dental/PARTHA-FOLLOWUP-OWNERSHIP-2026-08-21.md),
        which is exactly what the org's followup_ownership config already
        expresses for an in-window case (relation 'window_read', mayAct
        FALSE). This panel matches that posture rather than inventing a
        second, looser one for calls.
      */}
      <InboundCallPanel
        activity={view.inboundCalls}
        variant="monitor"
        leadDetailReachable={leadDetailReachable}
      />

      <p className="coc-station__footnote">
        Full record:{' '}
        <Link to="/dashboard/leads">open the Leads workspace</Link> - the
        station drills there without losing this queue (opens the same
        canonical Lead 360).
      </p>

      {drawer.kind === 'quick_view' ? (
        <LeadQuickViewHost
          leadId={drawer.row.leadId}
          seedTitle={drawer.row.displayName || `Lead #${drawer.row.leadId}`}
          onClose={() => setDrawer({ kind: 'none' })}
          onChanged={load}
        />
      ) : null}
      {drawer.kind === 'record_outcome' ? (
        <RecordOutcomeDialog
          row={drawer.row}
          outcomes={view.outcomes}
          onClose={() => setDrawer({ kind: 'none' })}
          onRecorded={load}
        />
      ) : null}
    </section>
  );
}
